/**
 * Defensive PostHog browser initializer.
 *
 * Mirrors the Sentry frontend pattern:
 * - initPostHog() returns void and NEVER throws.
 * - Missing VITE_POSTHOG_KEY = silent no-op.
 * - Init exceptions are logged and swallowed.
 *
 * Autocapture is OFF by default - the app uses manual capture() calls
 * via RouterPageviewTracker and (later) explicit business events.
 *
 * Usage from main.tsx (BEFORE ReactDOM.createRoot().render):
 *     import { initPostHog } from '@/observability/posthog'
 *     initPostHog()
 */

import type { PostHog } from 'posthog-js'

type PostHogState = {
  enabled: boolean
  key_configured: boolean
  init_attempted: boolean
  init_error: string | null
}

const state: PostHogState = {
  enabled: false,
  key_configured: false,
  init_attempted: false,
  init_error: null,
}

let client: PostHog | null = null

export function initPostHog(): void {
  state.init_attempted = true

  const env = import.meta.env as Record<string, string | undefined>
  const apiKey = (env.VITE_POSTHOG_KEY ?? '').trim()
  if (!apiKey) {
    console.info('[posthog] VITE_POSTHOG_KEY not set, skipping (no-op)')
    return
  }
  state.key_configured = true

  void (async () => {
    try {
      const mod = await import('posthog-js')
      const posthog = mod.default ?? mod

      const apiHost = (env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com').trim()
      const disabled =
        (env.VITE_POSTHOG_DISABLED ?? '').toLowerCase() === 'true' ||
        env.VITE_POSTHOG_DISABLED === '1'

      posthog.init(apiKey, {
        api_host: apiHost,
        // Manual capture only - matches backend pattern.
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: true,
        disable_session_recording: true,
        loaded: () => {
          state.enabled = !disabled
        },
      })

      if (disabled) {
        try {
          posthog.opt_out_capturing()
        } catch (err) {
          console.warn('[posthog] opt_out_capturing failed (swallowed):', err)
        }
      }

      client = posthog
      state.enabled = !disabled
      console.info(
        `[posthog] initialized host=${apiHost} disabled=${disabled}`,
      )
    } catch (err) {
      state.init_error = err instanceof Error ? `${err.name}: ${err.message}` : String(err)
      console.error('[posthog] init failed (app continues without PostHog):', err)
      client = null
    }
  })()
}

export function isPostHogEnabled(): boolean {
  return state.enabled && client !== null
}

export function getPostHogClient(): PostHog | null {
  return client
}

export function posthogStatus(): Readonly<PostHogState> {
  return { ...state }
}

export function capturePageview(pathname: string, search?: string): void {
  if (!isPostHogEnabled() || client === null) return
  try {
    const path = search ? `${pathname}${search}` : pathname
    client.capture('$pageview', { $current_url: path, path })
  } catch (err) {
    console.warn('[posthog] capturePageview failed (swallowed):', err)
  }
}

export function captureEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!isPostHogEnabled() || client === null) return
  if (!event) return
  try {
    client.capture(event, properties)
  } catch (err) {
    console.warn('[posthog] captureEvent failed (swallowed):', err)
  }
}

export function identify(
  distinctId: string,
  properties?: Record<string, unknown>,
): void {
  if (!isPostHogEnabled() || client === null) return
  if (!distinctId) return
  try {
    client.identify(distinctId, properties)
  } catch (err) {
    console.warn('[posthog] identify failed (swallowed):', err)
  }
}

/** Reset module state. Intended for unit tests only. */
export function resetForTests(): void {
  client = null
  state.enabled = false
  state.key_configured = false
  state.init_attempted = false
  state.init_error = null
}
